import React, { useMemo } from "react";
import { Form } from "react-bootstrap";
import SearchableListPicker from "@core/components/SearchableListPicker";
import InfoTooltip from "@core/components/InfoTooltip";
import { EntityUser } from "@features/user/types/user";
import { useInviteUserSearch } from "@features/admin/invitations/hooks/useInviteUserSearch";
import {
    INVITE_USER_SEARCH_EMPTY,
    INVITE_USER_SEARCH_ERROR,
    INVITE_USER_SEARCH_MIN_CHARS,
    inviteExistingUserPoolHint,
    inviteUserSearchMinCharsHint,
} from "@features/admin/invitations/constants/inviteModal";

interface InviteExistingUserFieldProps {
    ownScope: string;
    ownScopeRefId: number;
    enabled: boolean;
    selectedUser: EntityUser | null;
    onSelect: (user: EntityUser | null) => void;
    isInvalid?: boolean;
    errorText?: string;
}

const InviteExistingUserField: React.FC<InviteExistingUserFieldProps> = ({
    ownScope,
    ownScopeRefId,
    enabled,
    selectedUser,
    onSelect,
    isInvalid = false,
    errorText,
}) => {
    const { options, usersByUid, loading, error, setQuery } = useInviteUserSearch(
        ownScope,
        ownScopeRefId,
        enabled
    );

    const emptyText = useMemo(() => {
        if (error) return INVITE_USER_SEARCH_ERROR;
        return INVITE_USER_SEARCH_EMPTY;
    }, [error]);

    const selectedLabel = selectedUser
        ? [selectedUser.fname, selectedUser.lname].filter(Boolean).join(" ").trim() ||
          selectedUser.username ||
          selectedUser.email
        : undefined;

    return (
        <Form.Group controlId="invExistingUser" data-testid="invite-existing-user-field">
            <Form.Label>
                Who you are inviting<span className="text-danger">*</span>
                <InfoTooltip text="Search for someone who already has a role in this workspace, then choose their destination, department, and role." />
            </Form.Label>
            <SearchableListPicker
                options={options}
                value={selectedUser?.uid ?? null}
                selectedLabel={selectedLabel}
                onChange={(uid) => {
                    const user = usersByUid[Number(uid)];
                    onSelect(user ?? null);
                }}
                onSearchChange={setQuery}
                filterLocally={false}
                minQueryLength={INVITE_USER_SEARCH_MIN_CHARS}
                minQueryHint={inviteUserSearchMinCharsHint()}
                placeholder="Search by name, email, or TRN…"
                searchPlaceholder="Search by name, email, or TRN…"
                loading={loading}
                isInvalid={isInvalid}
                emptyText={emptyText}
                testId="invite-existing-user-picker"
            />
            <Form.Text className="text-muted" data-testid="invite-existing-user-pool-hint">
                {inviteExistingUserPoolHint(ownScope)}
            </Form.Text>
            {selectedUser && (
                <div
                    className="border rounded bg-light px-3 py-2 mt-2 small"
                    data-testid="invite-existing-user-summary"
                >
                    <div className="fw-semibold">
                        {[selectedUser.fname, selectedUser.lname].filter(Boolean).join(" ").trim() ||
                            selectedUser.username}
                    </div>
                    <div className="text-muted">
                        {selectedUser.email} · TRN {selectedUser.trn}
                    </div>
                </div>
            )}
            {errorText && (
                <div className="text-danger small mt-1" data-testid="invite-existing-user-error">
                    {errorText}
                </div>
            )}
        </Form.Group>
    );
};

export default InviteExistingUserField;
